import React, { useContext, useMemo, useState } from "react";

import { Button, Divider, Input, message, Typography } from "antd";
import { AppContext } from "App";
import blockIcon from "assets/images/block-3d.svg";
import checkIcon from "assets/images/check-3d.svg";
import checkOctIcon from "assets/images/check-octagon.svg";
import emailPurpIcon from "assets/images/email-purp.svg";
import emailWhiteIcon from "assets/images/email-white.svg";
import infoIcon from "assets/images/info-purp.svg";
import { useMtdRequirementCheckerReportMutation } from "services/potential-clients";
import { isEmailValid } from "utils/validation";

import { AnswersArr } from "./RequirementsChecker";

interface RequirementsCheckerResultProps {
    resultType: string,
    answers: AnswersArr,
    handleBack: () => void
}

const RESULT_CONFIG: Record<
    string,
    { icon: string; title: string; description: string }
> = {
    "MTD-not-relevant": {
        icon: blockIcon,
        title: "Making Tax Digital (MTD) is not currently relevant to you",
        description: "You do not need to start using Making Tax Digital for Income Tax until after you have submitted your first Self Assessment tax return.",
    },
    "MTD-not-registered": {
        icon: blockIcon,
        title: "You are not required to follow Making Tax Digital (MTD)",
        description: "Since you are not a sole trader or landlord, you're not required to follow Making Tax Digital (MTD). However, you may still need to file a Self Assessment tax return.",
    },
    "MTD-not-need": {
        icon: blockIcon,
        title: "You don't need to use Making Tax Digital for Income Tax",
        description: "If your income is under £10,000, you're not required to follow Making Tax Digital (MTD). However, you may still need to file a Self Assessment tax return.",
    },
    "MTD-to-early": {
        icon: checkIcon,
        title: "Making Tax Digital (MTD) doesn't apply to you yet. But you can sign up early.",
        description: "You're not yet required to follow Making Tax Digital (MTD), but you can choose to sign up early. We recommend preparing in advance.",
    },
    "MTD-2028": {
        icon: checkIcon,
        title: "Making Tax Digital (MTD) may apply to you starting from 6 April 2028",
        description: "Based on your income level, you may need to follow Making Tax Digital for Income Tax Self Assessment from 6 April 2028. We recommend preparing in advance.",
    },
    "MTD-2027": {
        icon: checkIcon,
        title: "Making Tax Digital (MTD) may apply to you starting from 6 April 2027",
        description: "Based on your income level, you may need to follow Making Tax Digital for Income Tax Self Assessment from 6 April 2027. We recommend preparing in advance.",
    },
    "MTD-2026": {
        icon: checkIcon,
        title: "You are MTD mandated from 6 April 2026",
        description: "Based on your income level, you'll need to follow Making Tax Digital for Income Tax Self Assessment from 6 April 2026. Don't worry, TaxZap will cover all your needs.",
    },
};

type SubscribeFormProps = {
  email: string;
  onEmailChange: React.ChangeEventHandler<HTMLInputElement>;
  onSubscribe: () => void;
  disabled: boolean;
  buttonText: string;
};

const SubscribeForm: React.FC<SubscribeFormProps> = ({
    email,
    onEmailChange,
    onSubscribe,
    disabled,
    buttonText,
}) => (
    <div className="requirements-checker__result__subscribe__form">
        <Input
            type="email"
            className="requirements-checker__result__subscribe__form__input"
            placeholder="Enter your email address"
            value={email}
            onChange={onEmailChange}
        />
        <Button
            type="primary"
            className="requirements-checker__result__subscribe__form__button"
            disabled={disabled}
            onClick={onSubscribe}
        >
            <span>{buttonText}</span>
            <img src={emailWhiteIcon} alt="info" className="ml4" />
        </Button>
    </div>
);

const RequirementsCheckerResult: React.FC<RequirementsCheckerResultProps> = ({ resultType, answers, handleBack }) => {
    const [email, setEmail] = useState("");
    const isValidEmail = useMemo(() => isEmailValid(email), [email]);

    const [sendMtdReport] = useMtdRequirementCheckerReportMutation();

    const handleSubscribe = async () => {
        await sendMtdReport({
            data: {
                email,
                resultType,
                form: answers,
            },
        }).unwrap().then(() => {
            setEmail("");
            message.success("Thank you! You can visit our blog to learn more about MTD.");
        }).catch(() => {
            message.error("Something went wrong. Please try again.");
        });
    };

    const { isMobile } = useContext(AppContext);

    const { icon, title, description } = RESULT_CONFIG[resultType] ?? {
        icon: checkIcon,
        title: "",
        description: "",
    };

    return (
        <>
            <img src={icon} width={isMobile ? 60 : 80} height={isMobile ? 60 : 80} alt="status" className={"mt20"} />
            <Typography className={`requirements-checker__title ${isMobile ? "mt20 taLeft" : "mt40"}`}>{title}</Typography>
            <Typography className={`requirements-checker__description ${isMobile ? "taLeft" : ""}`}>{description}</Typography>
            <div className="requirements-checker__result">
                {resultType === "MTD-not-relevant" && (
                    <>
                        <div className="requirements-checker__result__title">
                            <img src={infoIcon} alt="info" className="mr6 mb2"/>
                            <div>Stay Informed</div>
                        </div>
                        <div className="requirements-checker__result__description">
                            If you need to start self-assessment but don't have Unique Taxpayer Reference (UTR) get in touch with us via{" "}
                            <a className="purple-link" href="mailto:support@taxzap.co.uk" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>support@taxzap.co.uk</a>.
                        </div>
                        <div className="requirements-checker__result__subscribe">
                            <div className="requirements-checker__result__subscribe__title">
                                <img src={emailPurpIcon} alt="info" className="mr6 mb2"/>
                                Subscribe to TaxZap to get updates
                            </div>
                            <div className="requirements-checker__result__subscribe__description">
                                Get preparation guides, software recommendations, and deadline reminders
                            </div>
                            <SubscribeForm
                                email={email}
                                onEmailChange={e => setEmail(e.target.value)}
                                onSubscribe={handleSubscribe}
                                disabled={!isValidEmail}
                                buttonText="Subscribe"
                            />
                        </div>
                    </>
                )}
                {(
                    resultType === "MTD-not-registered" ||
                resultType === "MTD-not-need"
                ) && (
                    <>
                        <div className="requirements-checker__result__title">
                            <img src={infoIcon} alt="info" className="mr6 mb2"/>
                            <div>Stay Informed</div>
                        </div>
                        <div className="requirements-checker__result__description">
                            MTD might not affect you now, but it could in the future. Get the latest updates straight to your inbox.
                        </div>
                        <div className="requirements-checker__result__description">
                            If you need further help, please get in touch with us via{" "}
                            <a className="purple-link" href="mailto:support@taxzap.co.uk" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>support@taxzap.co.uk</a>.
                        </div>
                        <div className="requirements-checker__result__subscribe">
                            <div className="requirements-checker__result__subscribe__title">
                                <img src={emailPurpIcon} alt="info" className="mr6 mb2"/>
                                Subscribe to TaxZap to get updates
                            </div>
                            <div className="requirements-checker__result__subscribe__description">
                                Get preparation guides, software recommendations, and deadline reminders
                            </div>
                            <SubscribeForm
                                email={email}
                                onEmailChange={e => setEmail(e.target.value)}
                                onSubscribe={handleSubscribe}
                                disabled={!isValidEmail}
                                buttonText="Subscribe"
                            />
                        </div>
                    </>
                )}
                {resultType === "MTD-to-early" && (
                    <>
                        <div className="requirements-checker__result__title">
                            <img src={infoIcon} alt="info" className="mr6 mb2"/>
                            <div>Stay Informed</div>
                        </div>
                        <div className="requirements-checker__result__description">
                            Since your gross income is below £20,000, you don't have to use Making Tax Digital for Income Tax Self Assessment yet. But you can choose to sign up early.
                        </div>
                        <div className="requirements-checker__result__description">
                            If you need further help, please get in touch with us via{" "}
                            <a className="purple-link" href="mailto:support@taxzap.co.uk" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>support@taxzap.co.uk</a>.
                        </div>
                        <div className="requirements-checker__result__subscribe">
                            <div className="requirements-checker__result__subscribe__title">
                                <img src={emailPurpIcon} alt="info" className="mr6 mb2"/>
                                Join the waitlist for MTD preparation
                            </div>
                            <div className="requirements-checker__result__subscribe__description">
                                Our MTD app will help you track, record, and submit everything in one place.<br/>
                                Join the waitlist to be the first to try.
                            </div>
                            <SubscribeForm
                                email={email}
                                onEmailChange={e => setEmail(e.target.value)}
                                onSubscribe={handleSubscribe}
                                disabled={!isValidEmail}
                                buttonText="Join the waitlist"
                            />
                        </div>
                    </>
                )}
                {(
                    resultType === "MTD-2028" ||
                    resultType === "MTD-2027"
                ) && (
                    <>
                        <div className="requirements-checker__result__title mt30">
                            How to prepare:
                        </div>
                        <div className="requirements-checker__result__description-with-icon">
                            <img src={checkOctIcon} alt="check" className="mr6 mb2"/>
                            <div>Join the waitlist for TaxZap's MTD App</div>
                        </div>
                        <div className="requirements-checker__result__description-with-icon">
                            <img src={checkOctIcon} alt="check" className="mr6 mb2"/>
                            <div>Set up digital record-keeping for your income and expenses on TaxZap</div>
                        </div>
                        <div className="requirements-checker__result__description-with-icon">
                            <img src={checkOctIcon} alt="check" className="mr6 mb2"/>
                            <div>Understand what to submit and when</div>
                        </div>
                        <div className="requirements-checker__result__subscribe">
                            <div className="requirements-checker__result__subscribe__title">
                                <img src={emailPurpIcon} alt="info" className="mr6 mb2"/>
                                Join the waitlist for MTD preparation
                            </div>
                            <div className="requirements-checker__result__subscribe__description">
                                Our MTD app will help you track, record, and submit everything in one place.<br/>
                                Join the waitlist to be the first to try.
                            </div>
                            <SubscribeForm
                                email={email}
                                onEmailChange={e => setEmail(e.target.value)}
                                onSubscribe={handleSubscribe}
                                disabled={!isValidEmail}
                                buttonText="Join the waitlist"
                            />
                        </div>
                    </>
                )}
                {resultType === "MTD-2026" && (
                    <>
                        <div className="requirements-checker__result__title mt30">
                            How to prepare:
                        </div>
                        <div className="requirements-checker__result__description-with-icon">
                            <img src={checkOctIcon} alt="check" className="mr6 mb2"/>
                            <div>Join the waitlist for TaxZap's MTD App</div>
                        </div>
                        <div className="requirements-checker__result__description-with-icon">
                            <img src={checkOctIcon} alt="check" className="mr6 mb2"/>
                            <div>Set up digital record-keeping for your income and expenses on TaxZap</div>
                        </div>
                        <div className="requirements-checker__result__description-with-icon">
                            <img src={checkOctIcon} alt="check" className="mr6 mb2"/>
                            <div>Understand what to submit and when</div>
                        </div>
                        <div className="requirements-checker__result__subscribe">
                            <div className="requirements-checker__result__subscribe__title">
                                <img src={emailPurpIcon} alt="info" className="mr6 mb2"/>
                                Join the waitlist for MTD preparation
                            </div>
                            <div className="requirements-checker__result__subscribe__description">
                                Our MTD app will help you track, record, and submit everything in one place.<br/>
                                Join the waitlist to be the first to try.
                            </div>
                            <SubscribeForm
                                email={email}
                                onEmailChange={e => setEmail(e.target.value)}
                                onSubscribe={handleSubscribe}
                                disabled={!isValidEmail}
                                buttonText="Join the waitlist"
                            />
                        </div>
                    </>
                )}
                {!isMobile && <Divider/>}
                <div className="requirements-checker__questionnaire__buttons" style={{ gap: "0px" }}>
                    <button
                        className="requirements-checker__questionnaire__buttons__back-button"
                        onClick={handleBack}
                        style={{ width: isMobile ? "100%" : "124px" }}
                    >
                        <span>
                            Back
                        </span>
                    </button>
                    <div/>
                </div>
            </div>
        </>
    );
};

export default RequirementsCheckerResult;
